import { parseZonedDateTime } from '@rematter/common/dateTime';
import {
  PurchaseLoadSortOption,
  PurchaseLoadStatus,
  SettlementBasis,
  Weight,
  WorkOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type SortingQueueLineItem = {
  id: string;
  requiresSorting: boolean;
  settlementBasis: SettlementBasis;
  grossWeight: Weight | null;
  tareWeight: Weight | null;
  netWeight: Weight | null;
  item: {
    id: string;
    name: string;
    skuNumber: string | null;
  } | null;
  sortingWorkOrder: {
    id: string;
    status: WorkOrderStatus;
    workOrderNumber: string;
  } | null;
};

export type SortingQueuePurchaseLoad = {
  id: string;
  documentNumber: string;
  receivedDate: ReturnType<typeof parseZonedDateTime>;
  supplierAccount: {
    id: string;
    name: string;
  } | null;
  facility: {
    id: string;
    name: string;
  };
  lineItems: SortingQueueLineItem[];
};

export type SortingQueueFlattenedItem = {
  purchaseLoadId: string;
  purchaseLoadDocumentNumber: string;
  receivedDate: ReturnType<typeof parseZonedDateTime>;
  supplierAccountId: string | null;
  supplierName: string | null;
  facilityId: string;
  facilityName: string;
  lineItemId: string;
  itemId: string | null;
  itemName: string | null;
  itemSku: string | null;
  grossWeight: Weight | null;
  tareWeight: Weight | null;
  netWeight: Weight | null;
  settlementBasis: SettlementBasis;
  sortingWorkOrderId: string | null;
  sortingWorkOrderNumber: string | null;
  sortingWorkOrderStatus: WorkOrderStatus | null;
};

export const sortingQueueQueryAtom = createQueryAtomFactory(
  'trade.sortingQueue',
  (params: {
    facilityId?: string;
    facilityIds?: string[];
    supplierAccountIds?: string[];
    status?: PurchaseLoadStatus[];
    limit?: number;
    offset?: number;
    sorting?: PurchaseLoadSortOption[];
  }) => ({
    purchaseLoads: {
      __args: {
        filter: {
          facilityId: params.facilityId,
          facilityIds: params.facilityIds,
          supplierAccountIds: params.supplierAccountIds,
          status: params.status,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 100,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        receivedDate: true,
        supplierAccount: {
          id: true,
          name: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
        lineItems: {
          id: true,
          requiresSorting: true,
          settlementBasis: true,
          grossWeight: {
            value: true,
            unit: true,
          },
          tareWeight: {
            value: true,
            unit: true,
          },
          netWeight: {
            value: true,
            unit: true,
          },
          item: {
            id: true,
            name: true,
            skuNumber: true,
          },
          sortingWorkOrder: {
            id: true,
            status: true,
            workOrderNumber: true,
          },
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): {
      items: SortingQueueFlattenedItem[];
      totalCount: number;
      counts: {
        pending: number;
        inProgress: number;
        completed: number;
      };
    } => {
      if (!data?.purchaseLoads?.entries) {
        return {
          items: [],
          totalCount: 0,
          counts: { pending: 0, inProgress: 0, completed: 0 },
        };
      }

      const flattenedItems: SortingQueueFlattenedItem[] = [];
      let pendingCount = 0;
      let inProgressCount = 0;
      let completedCount = 0;

      for (const purchaseLoad of data.purchaseLoads.entries) {
        const lineItems = purchaseLoad.lineItems ?? [];
        const receivedDate = parseZonedDateTime(purchaseLoad.receivedDate);

        if (!receivedDate) {
          continue;
        }

        for (const lineItem of lineItems) {
          if (!lineItem.requiresSorting) {
            continue;
          }

          const workOrderStatus = lineItem.sortingWorkOrder?.status as
            | WorkOrderStatus
            | undefined;

          if (workOrderStatus === 'Completed') {
            completedCount++;
          } else if (
            workOrderStatus === 'Open' ||
            workOrderStatus === 'InProgress'
          ) {
            inProgressCount++;
          } else {
            pendingCount++;
          }

          flattenedItems.push({
            purchaseLoadId: purchaseLoad.id,
            purchaseLoadDocumentNumber: purchaseLoad.documentNumber,
            receivedDate,
            supplierAccountId: purchaseLoad.supplierAccount?.id ?? null,
            supplierName: purchaseLoad.supplierAccount?.name ?? null,
            facilityId: purchaseLoad.facility.id,
            facilityName: purchaseLoad.facility.facilityName ?? '',
            lineItemId: lineItem.id,
            itemId: lineItem.item?.id ?? null,
            itemName: lineItem.item?.name ?? null,
            itemSku: lineItem.item?.skuNumber ?? null,
            grossWeight: lineItem.grossWeight
              ? { ...lineItem.grossWeight, __typename: 'Weight' as const }
              : null,
            tareWeight: lineItem.tareWeight
              ? { ...lineItem.tareWeight, __typename: 'Weight' as const }
              : null,
            netWeight: lineItem.netWeight
              ? { ...lineItem.netWeight, __typename: 'Weight' as const }
              : null,
            settlementBasis: lineItem.settlementBasis as SettlementBasis,
            sortingWorkOrderId: lineItem.sortingWorkOrder?.id ?? null,
            sortingWorkOrderNumber:
              lineItem.sortingWorkOrder?.workOrderNumber ?? null,
            sortingWorkOrderStatus: workOrderStatus ?? null,
          });
        }
      }

      return {
        items: flattenedItems,
        totalCount: data.purchaseLoads.page.total ?? flattenedItems.length,
        counts: {
          pending: pendingCount,
          inProgress: inProgressCount,
          completed: completedCount,
        },
      };
    },
  },
);
